// frontend/src/hooks/useScreenShare.js
// WebRTC-based screen sharing + remote control hook.
// Both sharer and viewer roles are handled here.
// Wire socket events in a useEffect in the consuming component.
import { useRef, useState, useCallback } from 'react';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export function useScreenShare({ socket }) {
  const [isSharing, setIsSharing]     = useState(false);
  const [shareError, setShareError]   = useState('');
  const [remoteShares, setRemoteShares] = useState([]); // [{ peerId, peerName, stream }]
  const [remotePointers, setRemotePointers] = useState({}); // { peerId: { x, y } } — laser pointer overlay
  const [controlRequests, setControlRequests] = useState([]); // [{ from, fromName }]
  const [controlGranted, setControlGranted]   = useState(null); // peerId that has control

  const localStreamRef = useRef(null);
  const pcMap = useRef({});  // peerId → RTCPeerConnection
  const roomKeyRef = useRef(null);

  // ── Peer connection factory ───────────────────────────────────────────────
  const _closePeer = useCallback((peerId) => {
    const pc = pcMap.current[peerId];
    if (pc) { try { pc.close(); } catch (_) {} delete pcMap.current[peerId]; }
    setRemoteShares(prev => prev.filter(s => s.peerId !== peerId));
    setRemotePointers(prev => { const n = { ...prev }; delete n[peerId]; return n; });
  }, []);

  const _makePc = useCallback((peerId) => {
    if (pcMap.current[peerId]) pcMap.current[peerId].close();
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) socket.emit('webrtc-ice', { to: peerId, candidate });
    };

    pc.ontrack = ({ streams }) => {
      if (!streams[0]) return;
      setRemoteShares(prev => {
        const exists = prev.find(s => s.peerId === peerId);
        if (exists) return prev.map(s => s.peerId === peerId ? { ...s, stream: streams[0] } : s);
        return [...prev, { peerId, peerName: '', stream: streams[0] }];
      });
    };

    pc.onconnectionstatechange = () => {
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        _closePeer(peerId);
      }
    };

    pcMap.current[peerId] = pc;
    return pc;
  }, [socket, _closePeer]);

  // ── Start sharing my screen ────────────────────────────────────────────────
  const startShare = useCallback(async (roomKey) => {
    try {
      setShareError('');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15, cursor: 'always' },
        audio: false,
      });
      localStreamRef.current = stream;
      roomKeyRef.current = roomKey;
      setIsSharing(true);
      socket.emit('screen-share-start', { roomKey });
      // Auto-stop when user clicks browser's "Stop sharing" button
      stream.getVideoTracks()[0].onended = () => stopShare();
    } catch (e) {
      if (e.name !== 'NotAllowedError') setShareError('Screen share error: ' + e.message);
    }
  }, [socket]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopShare = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    Object.values(pcMap.current).forEach(pc => { try { pc.close(); } catch (_) {} });
    pcMap.current = {};
    setIsSharing(false);
    setControlGranted(null);
    if (roomKeyRef.current) socket.emit('screen-share-stop', { roomKey: roomKeyRef.current });
  }, [socket]);

  // ── Socket event handlers (wire these up in useEffect) ───────────────────
  // Viewer → we are the sharer, viewer requests our offer
  const onOfferRequest = useCallback(async ({ from }) => {
    if (!localStreamRef.current) return;
    const pc = _makePc(from);
    localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    socket.emit('webrtc-offer', { to: from, offer });
  }, [socket, _makePc]);

  // We are the viewer, receiving sharer's offer
  const onOffer = useCallback(async ({ from, offer }) => {
    const pc = _makePc(from);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket.emit('webrtc-answer', { to: from, answer });
  }, [socket, _makePc]);

  const onAnswer = useCallback(async ({ from, answer }) => {
    const pc = pcMap.current[from];
    if (pc && pc.signalingState !== 'stable') {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }, []);

  const onIce = useCallback(async ({ from, candidate }) => {
    const pc = pcMap.current[from];
    if (pc && candidate) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (_) {}
    }
  }, []);

  // A peer started sharing — we request their stream
  const onPeerShareStart = useCallback(({ peerId, peerName }) => {
    setRemoteShares(prev => {
      if (prev.find(s => s.peerId === peerId)) return prev;
      return [...prev, { peerId, peerName: peerName || 'Participant', stream: null }];
    });
    socket.emit('webrtc-offer-request', { to: peerId });
  }, [socket]);

  const onPeerShareStop = useCallback(({ peerId }) => {
    _closePeer(peerId);
  }, [_closePeer]);

  // ── Remote Pointer ────────────────────────────────────────────────────────
  const sendPointer = useCallback((peerId, x, y) => {
    socket.emit('remote-pointer', { to: peerId, x, y });
  }, [socket]);

  const onRemotePointer = useCallback(({ from, x, y }) => {
    setRemotePointers(prev => ({ ...prev, [from]: { x, y } }));
  }, []);

  // ── Remote Control ────────────────────────────────────────────────────────
  const requestControl = useCallback((peerId) => {
    socket.emit('remote-control-request', { to: peerId });
  }, [socket]);

  const respondControl = useCallback((from, accepted) => {
    socket.emit('remote-control-response', { to: from, accepted });
    if (accepted) setControlGranted(from);
    setControlRequests(prev => prev.filter(r => r.from !== from));
  }, [socket]);

  const onControlRequest = useCallback(({ from, fromName }) => {
    setControlRequests(prev => [...prev, { from, fromName }]);
  }, []);

  const onControlResponse = useCallback(({ accepted }) => {
    if (!accepted) alert('Remote control request was denied.');
  }, []);

  // Viewer sends a click to sharer
  const sendClick = useCallback((peerId, x, y) => {
    socket.emit('remote-click', { to: peerId, x, y, button: 0 });
  }, [socket]);

  const sendScroll = useCallback((peerId, dx, dy) => {
    socket.emit('remote-scroll', { to: peerId, dx, dy });
  }, [socket]);

  const sendKey = useCallback((peerId, keyEvt) => {
    socket.emit('remote-key', {
      to: peerId,
      key: keyEvt.key, code: keyEvt.code,
      ctrlKey: keyEvt.ctrlKey, shiftKey: keyEvt.shiftKey, altKey: keyEvt.altKey,
    });
  }, [socket]);

  // Sharer applies incoming click from controller
  const onRemoteClick = useCallback(({ x, y }) => {
    // x, y are normalized 0-1 relative to captured display
    // Map to window viewport
    const el = document.elementFromPoint(
      x * window.innerWidth,
      y * window.innerHeight
    );
    if (el) el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }, []);

  const onRemoteScroll = useCallback(({ dx, dy }) => {
    window.scrollBy(dx, dy);
  }, []);

  const onRemoteKey = useCallback(({ key, code, ctrlKey, shiftKey, altKey }) => {
    const active = document.activeElement || document.body;
    active.dispatchEvent(new KeyboardEvent('keydown', { key, code, ctrlKey, shiftKey, altKey, bubbles: true }));
  }, []);

  const cleanup = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    Object.values(pcMap.current).forEach(pc => { try { pc.close(); } catch (_) {} });
    pcMap.current = {};
    setIsSharing(false);
    setRemoteShares([]);
    setRemotePointers({});
    setControlRequests([]);
    setControlGranted(null);
  }, []);

  return {
    // State
    isSharing, shareError, remoteShares, remotePointers,
    controlRequests, controlGranted,
    // Actions — sharer side
    startShare, stopShare,
    respondControl, onRemoteClick, onRemoteScroll, onRemoteKey,
    // Actions — viewer side
    sendPointer, sendClick, sendScroll, sendKey, requestControl,
    // Socket event handlers
    onOfferRequest, onOffer, onAnswer, onIce,
    onPeerShareStart, onPeerShareStop,
    onRemotePointer, onControlRequest, onControlResponse,
    // Lifecycle
    cleanup,
  };
}
